import { Component, Input } from '@angular/core';

/**
 * Numeric unread badge for a navigation rail row.
 *
 * Hidden at zero, shows the number up to 99, and caps at `99+` above that.
 *
 * @example
 * ```html
 * <gains-rail-item-badge [count]="unreadCount" label="Notification"></gains-rail-item-badge>
 * ```
 */
@Component({
  selector: 'gains-rail-item-badge',
  standalone: false,
  templateUrl: './gains-rail-item-badge.component.html',
  styleUrl: './gains-rail-item-badge.component.scss'
})
export class GainsRailItemBadgeComponent {
  /** The count to display. Zero or less hides the badge entirely. */
  @Input() count = 0;

  /** Name of the row the badge belongs to, used to build the screen-reader text. */
  @Input() label = '';

  /**
   * Renders a plain dot instead of the number.
   *
   * A two-digit count needs ~22px, which does not fit beside a 12px icon in the 48px collapsed
   * rail; the dot keeps the "something is waiting" signal without the number.
   */
  @Input() dot = false;

  /** Above this the badge shows `99+` rather than growing the row. */
  private readonly MAX_DISPLAYED_COUNT = 99;

  /** Whether there is anything to show. */
  get hasCount(): boolean {
    return this.count > 0;
  }

  /** The text rendered inside the badge. */
  get displayCount(): string {
    return this.count > this.MAX_DISPLAYED_COUNT ? `${this.MAX_DISPLAYED_COUNT}+` : `${this.count}`;
  }

  /** Screen-reader text, announced when the count changes. */
  get ariaLabel(): string {
    const unread = `${this.count} unread`;
    return this.label ? `${this.label}: ${unread}` : unread;
  }
}
